import { useState } from "react";
import { format } from "date-fns";
import { CalendarIcon, X } from "lucide-react";
import { cn } from "@lib/utils";
import { Calendar } from "@components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@components/ui/popover";

interface DatePickerInputProps {
  id?: string;
  selected: Date | null | undefined;
  onChange: (date: Date | null) => void;
  minDate?: Date | null;
  maxDate?: Date | null;
  isClearable?: boolean;
  placeholderText?: string;
  dateFormat?: string;
  className?: string;
  disabled?: boolean;
  showYearDropdown?: boolean;
  yearDropdownItemNumber?: number;
  title?: string;
}

export function DatePickerInput({
  id,
  selected,
  onChange,
  minDate,
  maxDate,
  isClearable,
  placeholderText = "Pick a date",
  dateFormat = "PPP",
  className,
  disabled,
  showYearDropdown,
  yearDropdownItemNumber = 10,
  title,
}: DatePickerInputProps) {
  const [open, setOpen] = useState(false);

  const date = selected ?? undefined;

  const handleSelect = (selectedDate: Date | undefined) => {
    if (selectedDate) {
      onChange(selectedDate);
      setOpen(false);
    }
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(null);
  };

  const currentYear = new Date().getFullYear();
  const fromYear = showYearDropdown ? currentYear - (yearDropdownItemNumber ?? 10) : currentYear - 10;
  const toYear = currentYear + 20;

  const calendarDisabled: Array<{ before: Date } | { after: Date }> = [];
  if (minDate) calendarDisabled.push({ before: minDate });
  if (maxDate) calendarDisabled.push({ after: maxDate });

  const formatStr = dateFormat === "yyyy-MM-dd" ? "yyyy-MM-dd"
    : dateFormat === "MMM d, yyyy" ? "MMM d, yyyy"
    : "PPP";

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          id={id}
          title={title}
          disabled={disabled}
          className={cn(
            "flex items-center justify-between border border-border rounded px-3 py-2 text-sm text-left",
            !date && "text-muted-foreground",
            disabled && "opacity-50 cursor-not-allowed",
            className
          )}
        >
          <span className="flex items-center gap-2">
            <CalendarIcon className="h-4 w-4" />
            {date ? format(date, formatStr) : placeholderText}
          </span>
          {isClearable && date && !disabled && (
            <X className="h-4 w-4 opacity-50 hover:opacity-100" onClick={handleClear} />
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start" side="bottom">
        <Calendar
          mode="single"
          selected={date}
          onSelect={handleSelect}
          defaultMonth={date}
          captionLayout={showYearDropdown ? "dropdown" : "label"}
          startMonth={new Date(fromYear, 0)}
          endMonth={new Date(toYear, 11)}
          disabled={calendarDisabled.length > 0 ? calendarDisabled : undefined}
        />
      </PopoverContent>
    </Popover>
  );
}
